import { isHebrew } from "../utils/headline";
import { useEffect, useMemo, useRef, useState } from "react";
import { useMarketStatus } from "../utils/marketStatus";
import { dirOf } from "../utils/textDir";
import { T, UI, FONT } from "../constants/tokens";
import { TYPE } from "../constants/typography";
import { portfolioMatches, eventNames, isAbout } from "../utils/newsMatch";
import { clusterFor } from "../utils/newsCluster";
import { useClusterSummary } from "../utils/clusterSummary";
import { fetchCompanyNews } from "../utils/finnhubNews";
import { fetchQuote } from "../utils/finnhub";
import { fetchNextReport } from "../utils/nextReport";
import { fetchGeoEvents } from "../utils/geoEvents";
import { daysUntilText } from "../utils/homeChanges";
import { quotedHeadline } from "../utils/voice";
import { timeAgoSec } from "../utils/timeAgo";
import { dateNumeric } from "../utils/formatDate";
import { isAdvisoryArticle } from "../constants/advisory";
import { filingForArticle } from "../utils/filingMatch";
import PortfolioTie from "./PortfolioTie";
import FilingSummary from "./FilingSummary";
import LTR from "./LTR";
import { Amount } from "./Money";
import { fmtPct } from "../utils/formatters";
import { Skeleton, Loading } from "./Skeleton";

// ─── The article window, below the headline ──────────────────────────────────
// In this order (David, 2026-09-25):
//   2 · בקצרה          · the event in Suricata's voice, every sentence naming
//                        its source, built only from the reports' own
//                        summaries. Not ready in 8s → the report's summary.
//     · בדיווח הרשמי של החברה ל-SEC · sprint 21: an 8-K / 6-K of the same
//                        company within two days of the article, with what
//                        it said and the link. Absent when there is none.
//   3 · בתיק שלך       · weight, and the other holdings the event touches.
//                        Absent when there are none.
//   4 · בהקשר          · facts already in the app: price and today's move, the
//                        next report, this week's article count, a related
//                        geopolitical event.
//   5 · מה כתבו על זה  · every report in the cluster, with its link.
//   6 · לקריאה המלאה   · the one primary action: the article at its source.
//
// Context, not interpretation. Nothing here says a report "affects", is
// "positive/negative for" or "matters to" anything (VISION.md).

// How long the cluster waits for the company feeds before it is formed. The
// summary's own deadline (6s) starts after that · 8 seconds from opening.
const POOL_WAIT_MS = 2000;

function Head({ children, note }) {
  return (
    <div style={{ display: "flex", alignItems: "baseline", justifyContent: "space-between", gap: 8, marginBottom: 8 }}>
      <h3 style={{ ...TYPE.headingSub, margin: 0, fontSize: 13 }}>{children}</h3>
      {note && <span style={{ ...TYPE.caption }}>{note}</span>}
    </div>
  );
}

// Why there is no "בקצרה" (sprint 14, 2.4) · useClusterSummary's reason. A
// short single-source summary ("single") has no note: the report's own
// summary is on screen, and nothing was asked for (20/10/2026).
const NO_BRIEF = {
  timeout: "אין סיכום של סוריקטה: הסיכום לא התקבל בזמן.",
  failed: "אין סיכום של סוריקטה: הסיכום אינו זמין כרגע.",
  checks: "אין סיכום של סוריקטה: הסיכום לא עבר את הבדיקות, ולכן אינו מוצג.",
};

const Block = ({ children }) => (
  <section style={{ borderTop: `1px solid ${UI.line.hairline}`, paddingTop: 12 }}>{children}</section>
);

export default function ArticleReader({
  article, pool = [], portfolio = [], watchlist = [],
  excerptText, headlineOf, onOpenStock, filings = [], hebrewOnly = false,
}) {
  const market = useMarketStatus();
  // Keyed on the symbols and names as a STRING. The portfolio array gets a new
  // identity on every 30-second price refresh (once per symbol); keyed on the
  // array, the window reset while it was being read · the reports list
  // vanished, the brief went back to loading, everything was fetched again
  // (review, 25.09).
  const idKey = [...portfolio, ...watchlist].map(s => `${s.symbol}=${s.name || ""}`).join("|");
  const names = useMemo(() => Object.fromEntries(idKey.split("|").filter(Boolean).map(x => {
    const [sym, name] = x.split("=");
    return [sym, name && name !== sym ? name : ""];
  })), [idKey]);
  const mine = useMemo(() => Object.keys(names), [names]);

  // The companies this article is about: the user's own first, then Finnhub's
  // tag on a general-feed article.
  const subjectKey = useMemo(() => {
    const m = portfolioMatches(article, mine, names);
    return (m.length ? m : article.feed === "market" ? (article.symbols || []).slice(0, 2) : []).join(",");
  }, [article, mine, names]);
  const subjects = useMemo(() => (subjectKey ? subjectKey.split(",") : []), [subjectKey]);
  const filing = useMemo(() => filingForArticle(article, subjects, filings), [article, subjects, filings]);

  // ── The cluster: the feed on screen plus the subjects' company feeds ──────
  const [extra, setExtra] = useState(null);           // null = still loading
  useEffect(() => {
    let alive = true;
    setExtra(null);
    const syms = subjects.slice(0, 3);
    if (!syms.length) { setExtra([]); return; }
    const t = setTimeout(() => { if (alive) setExtra(prev => prev ?? []); }, POOL_WAIT_MS);
    Promise.all(syms.map(s => fetchCompanyNews(s, { days: 3 }).catch(() => [])))
      .then(lists => { if (alive) setExtra(prev => prev ?? lists.flat()); });
    return () => { alive = false; clearTimeout(t); };
  }, [subjects]);

  // The cluster is formed once per article and then held (sprint 14, 2.4).
  // The pool is read when the cluster forms, not followed: when the feed
  // behind the window changed (the "my" tab loading, a refresh), the cluster
  // gained a member, its id changed, and a brief already on screen went back
  // to loading and, when the new request did not answer in time, was
  // replaced by "מתוך הכתבה".
  const poolRef = useRef(pool);
  poolRef.current = pool;
  const cluster = useMemo(() => {
    if (extra === null) return null;
    const words = subjects.flatMap(s => [s.toLowerCase(), String(names[s] || "").split(/[\s,.]+/)[0].toLowerCase()]).filter(Boolean);
    return clusterFor(article, [...poolRef.current, ...extra], { companyWords: words });
  }, [article, extra, subjects, names]);

  const brief = useClusterSummary(cluster, article);
  // One source: the label says it is the AI's summary of that report's own
  // summary, not of an event several outlets reported.
  const briefLabel = brief.kind === "single"
    ? <>סיכום <LTR>AI</LTR> של התקציר מ-<LTR>{article.source}</LTR> · ייתכנו אי-דיוקים</>
    : "בקצרה · סיכום של סוריקטה";

  // The cluster without the open article (by id, address or headline).
  const others = useMemo(() => {
    const norm = t => String(t || "").toLowerCase().replace(/\s+/g, " ").trim();
    const self = norm(article.title);
    // Hebrew headlines only (26/10/2026): a report not translated yet is not listed.
    return (cluster || []).filter(a =>
      !(a === article || (a.id && a.id === article.id) || (a.url && a.url === article.url) || (self && norm(a.title) === self))
      && (!hebrewOnly || !headlineOf || isHebrew(headlineOf(a))));
  }, [cluster, article, headlineOf, hebrewOnly]);

  // ── In your portfolio: every holding any report in the cluster is about ──
  // An article worded as advice gets no personal context (sprint 13, 1.5 ·
  // constants/advisory.js): its window does not say which holdings it
  // touches or what share of the portfolio they are.
  const advisory = isAdvisoryArticle(article, headlineOf ? headlineOf(article) : undefined);
  const touched = useMemo(() => {
    if (advisory) return [];
    const set = new Set();
    for (const a of cluster || [article]) for (const s of portfolioMatches(a, mine, names)) set.add(s);
    return [...set];
  }, [cluster, article, mine, names, advisory]);
  const held = touched.filter(s => portfolio.some(p => p.symbol === s));
  const watched = touched.filter(s => !held.includes(s));
  const total = portfolio.reduce((a, p) => a + (p.quote !== "none" ? (p.currentPrice || 0) * (p.shares || 0) : 0), 0);
  const heldPct = total > 0 ? held.reduce((a, s) => {
    const p = portfolio.find(x => x.symbol === s);
    return a + (p && p.quote !== "none" ? (p.currentPrice || 0) * (p.shares || 0) : 0);
  }, 0) / total * 100 : null;

  // ── Context, for the first company the article is about ──────────────────
  // None for an article worded as advice or a list (sprint 14, 2.1): the
  // subjects come from the reader's own holdings, and a price and a next
  // report beside "how many shares of Apple you'd need" is a tie to the
  // portfolio by another name.
  const subject = advisory ? null : held[0] || touched[0] || subjects[0] || null;
  const [ctx, setCtx] = useState(null);
  useEffect(() => {
    let alive = true;
    setCtx(null);
    if (!subject) return;
    Promise.all([
      fetchQuote(subject).catch(() => null),
      fetchNextReport(subject).catch(() => null),
      fetchCompanyNews(subject, { days: 7 }).catch(() => []),
      fetchGeoEvents().catch(() => []),
    ]).then(([q, rep, week, geo]) => {
      if (!alive) return;
      const name = names[subject] || "";
      const since = Date.now() / 1000 - 7 * 86400;
      const count = week.filter(a => a.datetime >= since && isAbout(a, subject, name)).length;
      const ev = (geo || []).find(e => e.category && e.category !== "none" && eventNames(e, subject, name)) || null;
      setCtx({ q, rep, count, ev });
    });
    return () => { alive = false; };
  }, [subject, names]);  // names is keyed on a string (idKey), stable across price refreshes

  const dayPct = ctx?.q?.c > 0 && ctx.q.pc > 0 ? ((ctx.q.c - ctx.q.pc) / ctx.q.pc) * 100 : null;

  return (
    <>
      {/* 2 · בקצרה */}
      <section>
        {brief.state === "ready" ? (
          <div style={{ background: UI.accent.soft, borderRadius: 8, padding: "12px 16px" }}>
            <Head note={brief.kind === "single" ? null : `${cluster.length} מקורות`}>
              <span style={{ color: UI.accent.base }}>{briefLabel}</span>
            </Head>
            <div style={{ fontSize: 15, color: UI.text.primary, lineHeight: 1.85 }}>
              {brief.sentences.map((s, i) => <span key={i}>{s.text}{i < brief.sentences.length - 1 ? " " : ""}</span>)}
            </div>
            <div style={{ ...TYPE.caption, marginTop: 8 }}>
              {brief.kind === "single"
                ? "נבנה רק מהכותרת והתקציר שהמקור פרסם."
                : "נבנה רק מהכותרות והתקצירים שהמקורות פרסמו. כל משפט נוקב במקור שלו."}
            </div>
          </div>
        ) : brief.state === "loading" ? (
          <div style={{ background: UI.accent.soft, borderRadius: 8, padding: "12px 16px" }}>
            <Head><span style={{ color: UI.accent.base }}>{briefLabel}</span></Head>
            <Loading>{[92, 80, 64].map((w, i) => <Skeleton key={i} w={`${w}%`} h={10} mb={7}/>)}</Loading>
          </div>
        ) : brief.state === "forming" ? (
          // Not yet known whether there will be a brief: no title that might
          // have to be taken back (sprint 14, 2.4).
          <div aria-busy="true" aria-label="טוען">
            <Loading>{[92, 80, 64].map((w, i) => <Skeleton key={i} w={`${w}%`} h={10} mb={7}/>)}</Loading>
          </div>
        ) : (
          // No brief: the report's own summary, attributed, and one line that
          // says why there is no brief. Never an empty box.
          <div>
            {excerptText && <Head>מתוך הכתבה · <LTR>{article.source}</LTR></Head>}
            {excerptText && <div dir={dirOf(excerptText)} style={{ fontSize: 15, color: UI.text.secondary, lineHeight: 1.8 }}>{excerptText}</div>}
            {/* A short single-source summary is the ordinary case and needs
                no explanation. A brief that was asked for and did not come
                says why; no summary at all says so. */}
            {brief.reason === "single" ? (
              !excerptText && <div style={{ ...TYPE.caption }}>אין תקציר לכתבה הזו. הכתבה המלאה בקישור למטה.</div>
            ) : (
              <div style={{ ...TYPE.caption, marginTop: excerptText ? 8 : 0 }}>
                {NO_BRIEF[brief.reason] || NO_BRIEF.failed}{excerptText ? "" : " · אין תקציר לכתבה הזו. הכתבה המלאה בקישור למטה."}
              </div>
            )}
          </div>
        )}
      </section>

      {/* The company's own report on the same event (sprint 21) */}
      {filing && (
        <Block>
          <Head>בדיווח הרשמי של החברה ל-<LTR>SEC</LTR></Head>
          <FilingSummary filing={filing}/>
          <div style={{ ...TYPE.caption, marginTop: 8 }}>סיכום <LTR>AI</LTR> של הדיווח עצמו, וכל משפט נוקב במסמך שלו.</div>
        </Block>
      )}

      {/* 3 · בתיק שלך */}
      {(held.length > 0 || watched.length > 0) && (
        <Block>
          <Head>בתיק שלך</Head>
          <div style={{ display: "flex", flexDirection: "column", gap: 6 }}>
            {held.length > 0 && <PortfolioTie symbols={held} weight={heldPct} max={5}/>}
            {watched.length > 0 && <PortfolioTie symbols={watched} held={false} max={5}/>}
            {onOpenStock && (
              <div style={{ display: "flex", gap: 14, flexWrap: "wrap" }}>
                {touched.map(sym => (
                  <button key={sym} onClick={() => onOpenStock(sym)} style={LINK}>
                    עמוד החברה · <LTR>{sym}</LTR><span aria-hidden style={{ fontSize: 16, lineHeight: 1 }}>←</span>
                  </button>
                ))}
              </div>
            )}
          </div>
        </Block>
      )}

      {/* 4 · בהקשר */}
      {subject && (
        <Block>
          <Head note={<>על <LTR>{subject}</LTR></>}>בהקשר</Head>
          {!ctx ? (
            <Skeleton h={40}/>
          ) : (
            <div style={{ display: "flex", flexDirection: "column", gap: 6, fontSize: 14, color: UI.text.primary, lineHeight: 1.65 }}>
              {ctx.q?.c > 0 && (
                <div>
                  מחיר אחרון <Amount value={ctx.q.c} currency="USD" decimals={2} size={null} weight={700}/>
                  {dayPct != null && (Math.abs(dayPct) < 0.005
                    ? <span style={{ color: UI.text.muted }}> · {market.open ? "ללא שינוי היום" : "ללא שינוי ביום המסחר האחרון"}</span>
                    : <> · {market.open ? "היום" : "ביום המסחר האחרון"} <LTR className="si-num" style={{ fontWeight: 700, color: dayPct > 0 ? UI.gain : UI.loss }}>{fmtPct(dayPct)}</LTR></>)}
                  <span style={{ ...TYPE.caption }}> · <LTR>Finnhub</LTR></span>
                </div>
              )}
              {ctx.rep && !ctx.rep.none && ctx.rep.date && (
                <div>
                  הדוח הבא <LTR className="si-num" style={{ fontWeight: 700 }}>{dateNumeric(ctx.rep.date)}</LTR>
                  {daysUntilText(ctx.rep.date) && <> · {daysUntilText(ctx.rep.date)}</>}
                  <span style={{ ...TYPE.caption }}> · לוח הדוחות של <LTR>Finnhub</LTR></span>
                </div>
              )}
              <div>
                {ctx.count === 0 ? "אין כתבות על החברה ב-7 הימים האחרונים"
                  : ctx.count === 1 ? "כתבה אחת על החברה ב-7 הימים האחרונים"
                  : <><LTR className="si-num" style={{ fontWeight: 700 }}>{ctx.count}</LTR> כתבות על החברה ב-7 הימים האחרונים</>}
                <span style={{ ...TYPE.caption }}> · חדשות החברה ב-<LTR>Finnhub</LTR>, שהחברה בכותרת שלהן</span>
              </div>
              {ctx.ev && quotedHeadline(ctx.ev) && (
                <div>
                  אירוע גיאופוליטי שנוקב בחברה:{" "}
                  <span dir={quotedHeadline(ctx.ev).rtl ? "rtl" : "ltr"} style={{ fontWeight: 600 }}>{quotedHeadline(ctx.ev).text}</span>
                  {ctx.ev.source_name && <span style={{ ...TYPE.caption }}> · <LTR>{ctx.ev.source_name}</LTR></span>}
                </div>
              )}
            </div>
          )}
        </Block>
      )}

      {/* 5 · מה כתבו על זה · the OTHER reports. The open article is already
          on screen, so it is not listed again, nor is a syndicated copy of it
          (same headline). Nothing left → no section (2026-09-26). */}
      {others.length > 0 && (
        <Block>
          <Head note={others.length > 1 ? `${others.length} כתבות` : null}>מה כתבו על זה</Head>
          <div style={{ display: "flex", flexDirection: "column" }}>
            {others.map((a, i) => (
              <div key={a.id || a.url} style={{ padding: "8px 0", borderBottom: i < others.length - 1 ? `1px solid ${UI.line.hairline}` : "none" }}>
                <div style={{ display: "flex", gap: 6, alignItems: "baseline", flexWrap: "wrap", fontSize: 12, color: UI.text.muted }}>
                  <LTR style={{ fontWeight: 800, color: UI.text.secondary }}>{a.source}</LTR>
                  <span>·</span><span>{timeAgoSec(a.datetime)}</span>
                </div>
                <div dir={dirOf(headlineOf(a))} style={{ fontSize: 14, fontWeight: 700, color: UI.text.primary, lineHeight: 1.45, marginTop: 2 }}>{headlineOf(a)}</div>
                {a.url && (
                  <a href={a.url} target="_blank" rel="noopener noreferrer" style={{ fontSize: 13, fontWeight: 700, color: UI.interactive, textDecoration: "none", display: "inline-flex", minHeight: 32, alignItems: "center" }}>
                    לכתבה ב-<LTR>{a.source}</LTR>
                  </a>
                )}
              </div>
            ))}
          </div>
        </Block>
      )}

      {/* 6 · לקריאה המלאה · the one primary action */}
      {article.url && (
        <a href={article.url} target="_blank" rel="noopener noreferrer" style={{
          display: "flex", alignItems: "center", justifyContent: "center", gap: 8,
          minHeight: 48, borderRadius: T.radius.md, textDecoration: "none",
          background: UI.accent.base, color: UI.surface.page,
          fontSize: 15, fontWeight: 800, fontFamily: FONT.body,
        }}>
          <span>לקריאה המלאה ב-<LTR>{article.source}</LTR></span>
        </a>
      )}

      <div style={{ ...TYPE.caption, textAlign: "center" }}>מידע בלבד · אינו ייעוץ השקעות</div>
    </>
  );
}

const LINK = {
  background: "transparent", border: "none", padding: 0, minHeight: 44, fontSize: 14, fontWeight: 700,
  color: UI.interactive, cursor: "pointer", fontFamily: "inherit", display: "inline-flex", alignItems: "center", gap: 6,
};
